import {
  CustomerRequestedQuoteBodyDto,
  CustomerRequestedQuoteResponseDto,
  CustomerRequestedQuoteResponseDtoState,
  WithdrawQuoteBodyDto,
} from '@forto-dev/rates-client'

import Client from '@/api/client'
import { getConfig } from '@/app/config'
import { error } from '@/utils/logger'

import { ManualQuoteListFilterType } from './manual-quotes.service'
import {
  AcceptCustomerRequestedQuote,
  CreateCustomerRequestedQuote,
  GetCustomerRequestedQuoteById,
  GetCustomerRequestedQuotes,
  WithdrawCustomerRequestedQuote,
} from './types'

const normalizeState = (state: string): string => state.toLowerCase()

const PENDING_STATES = new Set<string>(
  [CustomerRequestedQuoteResponseDtoState.Pending, CustomerRequestedQuoteResponseDtoState.WaitingForCustomer].map(
    normalizeState
  )
)

const VALID_STATES = new Set<string>([CustomerRequestedQuoteResponseDtoState.Active].map(normalizeState))

const EXPIRED_STATES = new Set<string>(
  [CustomerRequestedQuoteResponseDtoState.Discarded, CustomerRequestedQuoteResponseDtoState.ValidityExpired].map(
    normalizeState
  )
)

const DECLINED_STATES = new Set<string>([CustomerRequestedQuoteResponseDtoState.RejectedByCustomer].map(normalizeState))

export class CustomerRequestedQuoteService {
  private readonly client: Client
  constructor(
    private readonly baseURL: string = '/api/rates-management',
    private readonly url: string = getConfig().API_ENDPOINT ?? ''
  ) {
    this.client = new Client(this.url)
  }
  async getCustomerRequestedQuotes() {
    try {
      const result = await this.client.get<GetCustomerRequestedQuotes[]>(
        `${this.baseURL}/api/customer-requested-quotes`
      )
      return result
    } catch (err) {
      error('Error fetching customer requested quotes:', err)
      throw err
    }
  }

  async getCustomerRequestedQuoteById(id: string) {
    try {
      const quote = await this.client.get<GetCustomerRequestedQuoteById>(
        `${this.baseURL}/api/customer-requested-quotes/${id}`
      )
      return quote || null
    } catch (err) {
      error('Error fetching customer requested quote:', err)
      throw err
    }
  }

  async createCustomerRequestedQuote(dto: CustomerRequestedQuoteBodyDto) {
    try {
      return await this.client.post<CreateCustomerRequestedQuote>(`${this.baseURL}/api/customer-requested-quotes`, dto)
    } catch (err) {
      error('Error creating customer requested quote:', err)
      throw err
    }
  }

  async withdrawCustomerRequestedQuote(quoteId: string, body: WithdrawQuoteBodyDto) {
    try {
      const result = await this.client.post<WithdrawCustomerRequestedQuote>(
        `${this.baseURL}/api/customer-requested-quotes/${quoteId}/withdraw`,
        body
      )
      return result
    } catch (err) {
      error('Error withdrawing customer requested quote:', err)
      throw err
    }
  }

  async acceptCustomerRequestedQuote(quoteId: string) {
    try {
      const result = await this.client.post<AcceptCustomerRequestedQuote>(
        `${this.baseURL}/api/customer-requested-quotes/${quoteId}/accept`,
        {}
      )
      return result
    } catch (err) {
      error('Error accepting customer requested quote:', err)
      throw err
    }
  }
}

export const isCustomerRequestedQuotePending = (quote: Pick<CustomerRequestedQuoteResponseDto, 'state'>): boolean => {
  return PENDING_STATES.has(normalizeState(quote.state))
}

export const isCustomerRequestedQuoteValid = (
  quote: Pick<CustomerRequestedQuoteResponseDto, 'state' | 'validTo'>
): boolean => {
  if (!quote.validTo) return false
  return VALID_STATES.has(normalizeState(quote.state)) && new Date(quote.validTo) > new Date()
}

export const isCustomerRequestedQuoteExpired = (quote: Pick<CustomerRequestedQuoteResponseDto, 'state'>): boolean => {
  return EXPIRED_STATES.has(normalizeState(quote.state))
}

export const isCustomerRequestedQuoteDeclined = (quote: Pick<CustomerRequestedQuoteResponseDto, 'state'>): boolean => {
  return DECLINED_STATES.has(normalizeState(quote.state))
}

export const isCustomerRequestedQuoteNotAvailable = (
  quote: Pick<CustomerRequestedQuoteResponseDto, 'state'>
): boolean => {
  void quote
  return false
}

export const isCustomerRequestedQuoteWithdrawn = (quote: Pick<CustomerRequestedQuoteResponseDto, 'state'>): boolean => {
  void quote
  return false
}

export const customerRequestedQuoteFilterMap: Record<
  ManualQuoteListFilterType,
  (quote: Pick<CustomerRequestedQuoteResponseDto, 'state' | 'validTo'>) => boolean
> = {
  [ManualQuoteListFilterType.ALL]: () => true,
  [ManualQuoteListFilterType.PENDING]: isCustomerRequestedQuotePending,
  [ManualQuoteListFilterType.VALID]: isCustomerRequestedQuoteValid,
  [ManualQuoteListFilterType.EXPIRED]: isCustomerRequestedQuoteExpired,
  [ManualQuoteListFilterType.DECLINED]: isCustomerRequestedQuoteDeclined,
  [ManualQuoteListFilterType.NOT_AVAILABLE]: isCustomerRequestedQuoteNotAvailable,
  [ManualQuoteListFilterType.WITHDRAWN]: isCustomerRequestedQuoteWithdrawn,
  [ManualQuoteListFilterType.RECENT]: () => true,
}

export const getFilteredCustomerRequestedQuotesByFilterType = <
  T extends Pick<CustomerRequestedQuoteResponseDto, 'state' | 'validTo' | 'createdAt'>,
>(
  quotes: T[],
  filterType: ManualQuoteListFilterType
): T[] => {
  return quotes.filter(customerRequestedQuoteFilterMap[filterType] || (() => true))
}

export const customerRequestedQuoteService = new CustomerRequestedQuoteService()
